import { FeatureExposureType } from '@/components/intro-wizards/types';
import { ROUTE_BUILD_ACTION_PLAN } from '@/constants/routes';
import { useRedirectIfQuestionnaireUncompleted } from '@/hooks/use-redirect-if-questionnaire-uncompleted';
import { useActionPlan } from '@/queries/action-plan.queries';
import {
  FeatureExposureStorageKey,
  useFeatureExposureCheck,
  useFeatureExposureMark,
} from '@/queries/feature-exposure.queries';
import { useCompleteInterventionMutation, useInterventions } from '@/queries/interventions.queries';
import useGetMe from '@/queries/me.queries';
import { useDisclosure } from '@cmpsr/components';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Intervention, InterventionStatus } from './types';

export default function useActionPlanController() {
  const { data: userData } = useGetMe();
  const { data: actionPlanData, isLoading: isLoadingActionPlan, refetch } = useActionPlan();
  const completeInterventionMutation = useCompleteInterventionMutation();
  const name = userData?.profile?.name;
  const { interventions, potentialRiskDecrease } = actionPlanData || {};
  const { isLoadingQuestionnaire } = useRedirectIfQuestionnaireUncompleted();
  const { isLoading: isLoadingInterventions } = useInterventions();
  const navigate = useNavigate();

  const { data: hasBeenShown, isLoading: isLoadingFeatureCheck } = useFeatureExposureCheck(
    FeatureExposureType.actionPlan,
    FeatureExposureStorageKey.ActionPlan,
  );
  const { mutate: markAsSeen } = useFeatureExposureMark(FeatureExposureType.actionPlan);
  const modalIntroWizardDisclosure = useDisclosure();

  useEffect(() => {
    if (hasBeenShown === false) {
      modalIntroWizardDisclosure.onOpen();
    } else {
      modalIntroWizardDisclosure.onClose();
    }
  }, [hasBeenShown, modalIntroWizardDisclosure]);

  function handleFinishIntroWizard() {
    markAsSeen();
    modalIntroWizardDisclosure.onClose();
    return navigate(ROUTE_BUILD_ACTION_PLAN);
  }

  const allInterventionsCompleted =
    !interventions?.length || interventions.every(({ status }) => status === InterventionStatus.COMPLETED);

  const handleCompleteIntervention = async (intervention: Intervention) => {
    try {
      await completeInterventionMutation.mutateAsync({ actionPlanInterventionId: intervention.id });
      await refetch();
    } catch (error) {
      console.error(error);
    }
  };

  const isLoading = isLoadingActionPlan || isLoadingInterventions || isLoadingQuestionnaire || isLoadingFeatureCheck;

  return {
    name,
    interventions,
    potentialRiskDecrease,
    isLoading,
    handleCompleteIntervention,
    allInterventionsCompleted,
    modalIntroWizardDisclosure,
    handleFinishIntroWizard,
  };
}
